import { ListboxMultiselect } from '@evertrue/et-components';
import useFetchAllUsers from 'apps/search/hooks/use-fetch-all-users';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { fetchUsersById } from 'apps/volunteers/vms-queries';

const UserSelector = ({ selected = [], onChange = () => {}, disabled = false }) => {
  const { users, fetchUsers } = useFetchAllUsers();
  const searchQueryTimeout = useRef(null);
  const searchActive = useRef(false);
  const [normalizedSelected, setNormalizedSelected] = useState([]);

  const allUsersMapped = useMemo(() => _.map(users, (user) => ({ value: user.id, label: user.name })), [users]);

  /** Normalize both versions of inputs */
  const normalizeSelected = useCallback(
    async (sel) => {
      if (!sel?.length) return [];

      // version 2: [2270, 2240]
      if (typeof sel[0] === 'number' || typeof sel[0] === 'string') {
        const result = await Promise.all(
          sel.map(async (id) => {
            const found = allUsersMapped.find((u) => u.value == id);
            let users;
            if (!found) {
              users = await fetchUsersById([id]);
            }
            return found ? found : { value: users[0].id || id, label: users[0].name || id };
          })
        );

        return result.filter(Boolean);
      }

      // version 1: [{ value: 2270, label: 'Aditya Karan' }]
      if (sel[0]?.value && sel[0]?.label) return sel;

      return [];
    },
    [allUsersMapped]
  );

  useEffect(() => {
    normalizeSelected(selected).then(setNormalizedSelected);
  }, [selected, allUsersMapped, normalizeSelected]);

  /** Optionally: convert back to same format as input */
  const handleChange = (newValues) => {
    // detect input format
    const isArrayOfIds = selected.length > 0 && (typeof selected[0] === 'number' || typeof selected[0] === 'string');

    if (isArrayOfIds) {
      onChange(newValues.map((u) => u.value)); // return as [2270, 2240]
    } else {
      onChange(newValues); // return as [{ value, label }]
    }
  };

  /** Build the available options */
  let user_arr;

  if (!searchActive.current) {
    const combinedUsers = _.uniq([...normalizedSelected, ...allUsersMapped], false, (user) => user.value);
    user_arr = _.sortBy(combinedUsers, (user) => !normalizedSelected.some((sel) => sel.value === user.value));
  } else {
    const filtered = allUsersMapped.filter((user) =>
      user.label.toLowerCase().includes(searchActive.current.toLowerCase())
    );
    user_arr = _.sortBy(filtered, (user) => !normalizedSelected.some((sel) => sel.value === user.value));
  }

  return (
    <div className="user-selector--box-wrapper">
      <ListboxMultiselect
        size="medium"
        emptyValue="Select User..."
        label="Select User"
        options={user_arr}
        onChange={handleChange}
        onSearch={(search) => {
          searchActive.current = search;
          if (searchQueryTimeout.current) clearTimeout(searchQueryTimeout.current);
          searchQueryTimeout.current = setTimeout(() => {
            fetchUsers(search, 0);
          }, 500);
        }}
        scrollFetch={(search) => {
          fetchUsers(search, users.length);
        }}
        searchable={true}
        values={normalizedSelected}
        disabled={disabled}
      />
    </div>
  );
};

UserSelector.propTypes = {
  selected: PropTypes.array,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

export default UserSelector;
